import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Combobox } from "@headlessui/react"
import { CampingStyleProps, SiteProps } from "../../../interfaces"
import SiteIcons from "../../SiteIcons"
import { CAMPING_STYLES, KINDS, RESERVATION_KINDS, reservationKind } from "~/constants"
import { AdminContext } from "~/contexts/AdminContext"
import generateSiteOptions, { sortedSites } from "./generateSiteOptions"
import formatCampingStyleName from "../../../utils/formatCampingStyleName"
import api from "../../../api2"
import Spinner from "../../ui/Spinner"
import InlineWarningNotification from "../components/InlineWarningNotification"
import parseDateTime from "~/utils/parseDateTime"
import { DateTime } from "luxon"
import { useDebouncedCallback } from "use-debounce"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { ReservationProps } from "~/interfaces"
import NewReservationProps from "~/interfaces/NewReservationProps"
import { LockClosedIcon } from "@heroicons/react/16/solid"

function classNames(...classes: (string | boolean | undefined | null)[]) {
  return classes.filter(Boolean).join(" ")
}

export interface SiteOption {
  key: string
  value: string
  title: string
  description?: string
  site: SiteProps
  isDisabled?: boolean
  selectedReservationType?: string // NOTE: this might be better as reservationKind
  // TODO: why are these here?
  selectedSitesForGroup?
  setSelectedSitesForGroup?
  required?: boolean
}

type RvKind = typeof KINDS.RV_BACKIN | typeof KINDS.RV_HYBRID | typeof KINDS.RV_PULLTHROUGH | typeof KINDS.RV_PULLIN

const SitesDropdown = ({
  label,
  selectedIds,
  setSelectedIds,
  reservation,
  selectedReservationType,
  required,
  disabled,
  fetchSitesAvailability,
  startDateString,
  endDateString,
  isSiteLocked,
}: {
  label?: string
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  reservation?: ReservationProps | NewReservationProps | null
  selectedReservationType?: reservationKind
  required?: boolean
  disabled?: boolean
  fetchSitesAvailability?: boolean
  startDateString?: string
  endDateString?: string
  isSiteLocked?: boolean
}) => {
  const { camp, sites, campingStyles, reservationStates } = useContext(AdminContext)
  const { newReservation: newReservationFlow } = useContext(NewReservationContext)
  const [query, setQuery] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<SiteOption[]>([])
  const isSelectingMultiple =
    selectedReservationType == RESERVATION_KINDS.GROUP || selectedReservationType == RESERVATION_KINDS.BLOCKED
  const [siteView, setSiteView] = useState(options)
  const [isNotificationVisible, setNotificationVisible] = useState(false)
  const [unavailableOptions, setUnavailableOptions] = useState<SiteOption[]>([])
  const newReservation = newReservationFlow ?? reservation
  const [startDate, setStartDate] = useState<DateTime>(
    startDateString
      ? parseDateTime(startDateString)
      : typeof newReservation?.start_date === "object"
        ? parseDateTime(newReservation.start_date?.toISOString())
        : parseDateTime(newReservation?.start_date),
  )
  const [endDate, setEndDate] = useState<DateTime>(
    endDateString
      ? parseDateTime(endDateString)
      : typeof newReservation?.end_date === "object"
        ? parseDateTime(newReservation.end_date?.toISOString())
        : parseDateTime(newReservation?.end_date),
  )

  const selectedOptions = useMemo(
    () => options.filter((option) => selectedIds.includes(option.key)),
    [selectedIds, options],
  )

  useEffect(() => {
    if (startDateString) {
      setStartDate(parseDateTime(startDateString))
    } else if (newReservation.start_date) {
      setStartDate(parseDateTime(
        typeof newReservation?.start_date === "object"
          ? newReservation.start_date?.toISOString()
          : newReservation.start_date
      ))
    }
  }, [startDateString, typeof newReservation?.start_date === "object" && newReservation?.start_date?.toISOString()])

  useEffect(() => {
    if (endDateString) {
      setEndDate(parseDateTime(endDateString))
    } else if (newReservation.end_date) {
      setEndDate(parseDateTime(
        typeof newReservation?.end_date === "object"
          ? newReservation.end_date?.toISOString()
          : newReservation.end_date
      ))
    }
  }, [endDateString, typeof newReservation?.end_date === "object" && newReservation?.end_date?.toISOString()])

  useEffect(() => {
    // NOTE: base case
    if (options.length === 0) {
      setOptions(generateSiteOptions(sites, newReservation, reservationStates, campingStyles, camp))
    }

    if (!(startDate?.toISO() && endDate?.toISO()) || !fetchSitesAvailability) {
      return
    }

    setIsLoading(true)

    const startString = startDate.toISO() as string
    const endString = endDate.toISO() as string
    debouncedAvailabilities(startString, endString)
  }, [startDate, endDate, fetchSitesAvailability])

  useEffect(() => {
    if (!isOpen && query) {
      setIsOpen(true)
    }
  }, [query])

  useEffect(() => {
    if (selectedOptions.some((o) => o.isDisabled) && fetchSitesAvailability) {
      setSelectedIds(selectedOptions.filter((o) => !o.isDisabled).map((o) => o.key))
      const newUnavailable = selectedOptions.filter((o) => o.isDisabled)
      setUnavailableOptions(newUnavailable)
      setNotificationVisible(true)
    } else if (
      isNotificationVisible &&
      unavailableOptions.every((o) => !options.find((option) => option.key === o.key)?.isDisabled)
    ) {
      setUnavailableOptions([])
      setNotificationVisible(false)
    } else if (isNotificationVisible && selectedIds.some((id) => !unavailableOptions.some((uo) => uo.key === id))) {
      setNotificationVisible(false)
    }
  }, [selectedOptions, options, unavailableOptions, isNotificationVisible])

  const debouncedAvailabilities = useDebouncedCallback((startString, endString) => {
    api.Sites.availabilities(camp.id, {
      startDate: startString,
      endDate: endString,
      ignoredReservationIds: newReservation?.id ? [newReservation.id] : [],
    })
      .then(({ sites: mSites }) => {
        const mOptions = mSites
          .sort((a, b) => sortedSites(a, b, campingStyles))
          .map((site) => ({
            key: site.id,
            value: site.id,
            title: site.name,
            description: site.is_available ? "" : "Unavailable for dates",
            site: sites?.find((s) => s.id === site.id),
            isDisabled: !site.is_available,
          }))

        setOptions(mOptions)
        setIsLoading(false)
      })
      .catch((err) => {
        setIsLoading(false)
      })
  }, 100)

  const filterSiteViewArray = (value: string | React.ChangeEvent<HTMLInputElement>) => {
    const query = typeof value === "string" ? value : value.target.value

    setQuery(query)

    setSiteView(
      options.filter((site) => site.title.trim().toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())),
    )
    setIsOpen(true)
  }

  const handleSitesSelection = (selectedSites: SiteOption[]) => {
    const newSelectedIds = selectedSites.reduce(
      (acc: string[], site: SiteOption) =>
        acc.includes(site.key) ? acc.filter((key) => key !== site.key) : (acc.push(site.key), acc),
      [],
    )

    setSelectedIds(newSelectedIds)
    setQuery("")
    setIsOpen(false)
  }

  const rvKinds = [
    KINDS.RENTAL_RV,
    KINDS.RV_BACKIN,
    KINDS.RV_HYBRID,
    KINDS.RV_PULLTHROUGH,
    KINDS.RV_PULLIN,
    KINDS.RV_STORAGE,
  ]

  let rvType: string

  if (rvKinds.includes(selectedOptions[0]?.site?.kind as RvKind)) {
    const selectedSitesCampingStyleId = selectedOptions.map((option) => option?.site?.camping_style_id)

    const selectedCampingStyle = campingStyles.find((style) => style.id === selectedSitesCampingStyleId[0])

    if (selectedCampingStyle?.name === CAMPING_STYLES.SEASONAL_RV) {
      rvType = "Seasonal RVs"
    } else if (selectedCampingStyle?.name === CAMPING_STYLES.RV) {
      rvType = "RVs"
    }
  } else {
    rvType = selectedOptions[0]?.site?.kind
      ? selectedOptions[0]?.site.kind.charAt(0).toUpperCase() + selectedOptions[0]?.site.kind.slice(1)
      : ""
  }

  const isSelectAllCampingStylesShow = () => {
    const selectedSitesCampingStyleId = selectedOptions.map((option) => option?.site?.camping_style_id)

    if (selectedSitesCampingStyleId.length === 0) return false

    return selectedSitesCampingStyleId.every((id) => id === selectedSitesCampingStyleId[0])
  }

  const selectAllCampingStylesCheck = (option: SiteOption) => {
    const selectedSitesCampingStyleId = selectedOptions.map((option) => option?.site?.camping_style_id)

    return selectedSitesCampingStyleId[0] === option?.site?.camping_style_id
  }

  const handleSingleSelect = (option: SiteOption) => {
    setSelectedIds([option.key])
    setIsOpen(false)
    setQuery("")
  }

  useEffect(() => {
    setSiteView(options)
  }, [options])

  const handleBackspaceRemove = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === "Backspace" &&
      query === "" &&
      selectedIds.length > 0 &&
      !disabled
    ) {
      setSelectedIds(selectedIds.slice(0, -1))
      setIsOpen(true)
      e.preventDefault()
    }
  }, [query, selectedIds, disabled])

  return (
    <>
      <InlineWarningNotification
        onClose={() => setNotificationVisible(false)}
        isVisible={isNotificationVisible}
        unavailableOptions={unavailableOptions}
      />
      <div>
        {isSelectingMultiple ? (
          <Combobox as="div" multiple value={selectedOptions} onChange={handleSitesSelection} disabled={disabled}>
            {({ open }) => (
              <>
                <div className="flex justify-between items-end">
                  <div className="flex items-center">
                    <Combobox.Label className="block font-bold text-mdbase text-gray-900">
                      {label}s {required && <span className="text-red-600">*</span>}
                    </Combobox.Label>
                    {isLoading && <Spinner size={3} className="ml-2" />}
                  </div>
                  <div>
                    {isSelectAllCampingStylesShow() ? (
                      <span
                        className={classNames(
                          "ml-3.5 mb-0.5 inline underline text-xs text-gray-500 cursor-pointer hover:text-gray-900",
                        )}
                        onClick={() => {
                          let newArray: string[] = []

                          options.map((option) => {
                            if (!option.description && selectAllCampingStylesCheck(option)) newArray.push(option.key)
                          })

                          setSelectedIds(newArray)
                        }}
                      >
                        Select all {formatCampingStyleName(rvType)}
                      </span>
                    ) : null}
                    <span
                      className={classNames(
                        "ml-3.5 mb-0.5 inline underline text-xs text-gray-500 cursor-pointer hover:text-gray-900",
                      )}
                      onClick={() => {
                        let newArray: string[] = []

                        options.map((option) => {
                          if (!option.description) newArray.push(option.key)
                        })

                        setSelectedIds(newArray)
                      }}
                    >
                      Select all
                    </span>
                    <span
                      className={classNames(
                        "ml-3.5 mb-0.5 inline underline text-xs text-gray-500 cursor-pointer hover:text-gray-900",
                      )}
                      onClick={() => {
                        setSelectedIds([])
                      }}
                    >
                      Remove all
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <Combobox.Button className="w-full">
                    <Combobox.Input
                      data-testid="sites-dropdown-input-multiple"
                      className={classNames(
                        "w-full rounded-md border-0 bg-white pl-3 text-gray-900 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:leading-6 h-10.5",
                        disabled && "opacity-50 bg-gray-200",
                      )}
                      onChange={filterSiteViewArray}
                      autoComplete="off"
                      value={query !== "" ? query : selectedOptions.map((option) => option.title).join(", ")}
                      onClick={() => setIsOpen(true)}
                      onBlur={() => setIsOpen(false)}
                      onKeyDown={handleBackspaceRemove}
                    />
                  </Combobox.Button>
                  {siteView.length > 0 && open && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onMouseDown={e => {
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                        style={{ pointerEvents: "auto" }}
                      />
                      <Combobox.Options
                        onMouseDown={(e) => e.preventDefault()}
                        static
                        className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm focus:border-transparent"
                      >
                        {siteView.map((option, index) => (
                          <Combobox.Option
                            data-testid={`sites-dropdown-option-multiple-${index}`}
                            key={option.key}
                            value={option}
                            disabled={option.isDisabled}
                            className={({ active, selected }) =>
                              classNames(
                                "relative select-none py-2 px-3",
                                option.description ? "opacity-50" : "cursor-pointer",
                                selectedOptions.some((siteOption) => siteOption.key == option.key) && "bg-green-100",
                                active && "bg-gray-100",
                              )
                            }
                          >
                            {({ active, selected }) => (
                              <div className="flex justify-between">
                                <div className="flex flex-col">
                                  <span className={classNames("grow truncate text-md", selected && "font-semibold")}>
                                    {option.title}
                                  </span>
                                  <span className={classNames("truncate text-red-400 text-xs")}>
                                    {option.description}
                                  </span>
                                </div>
                                <SiteIcons
                                  site={option.site}
                                  size="10"
                                  isDropdown
                                  campingStyle={(campingStyles as CampingStyleProps[]).find(
                                    (style) => style.id == option?.site?.camping_style_id,
                                  )}
                                  lengthUnit={camp.length_unit}
                                />
                              </div>
                            )}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </>
                  )}
                </div>
              </>
            )}
          </Combobox>
        ) : (
          <Combobox as="div" value={selectedOptions[0]} onChange={handleSingleSelect} disabled={disabled}>
            {({ open }) => (
              <>
                <div className="flex items-center">
                  <Combobox.Label
                    className={"flex font-bold leading-6 text-gray-900"}
                    style={{ fontSize: ".92857143em" }}
                  >
                    {label} <span className="text-red-600">*</span>
                    {isSiteLocked && (
                      <div className="flex items-center">
                        <span className="text-gray-600 ml-2.5 text-md italic">Site locked</span>
                        <LockClosedIcon className="h-4 w-4 text-gray-500 ml-2" />
                      </div>
                    )}
                  </Combobox.Label>
                  {isLoading && <Spinner size={3} className="ml-2" />}
                </div>
                <div className="relative">
                  <Combobox.Button className="w-full ghostinspector-new-reservation-site-button z-10">
                    <Combobox.Input
                      data-testid="sites-dropdown-input-single"
                      className={classNames(
                        "w-full rounded-md border border-gray-300 bg-white pl-3 text-gray-900 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:leading-6 h-10.5 focus:border-transparent",
                        disabled && "opacity-50 bg-gray-200",
                        isSiteLocked && "opacity-70 bg-gray-200 focus:ring-gray-400",
                      )}
                      onChange={filterSiteViewArray}
                      value={query !== "" ? query : selectedOptions[0]?.title || ""}
                      onClick={() => setIsOpen(true)}
                      onBlur={() => setIsOpen(false)}
                      onKeyDown={handleBackspaceRemove}
                    />
                  </Combobox.Button>
                  {siteView.length > 0 && open && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onMouseDown={e => {
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                        onClick={e => {
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                        style={{ pointerEvents: "auto" }}
                      />
                      <Combobox.Options
                        static
                        onMouseDown={(e) => e.preventDefault()}
                        className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-none sm:text-sm"
                      >
                        {siteView.map((option, index) => (
                          <Combobox.Option
                            data-testid={`sites-dropdown-option-single-${index}`}
                            key={option.key}
                            value={option}
                            disabled={option.isDisabled}
                            className={({ active, selected }) =>
                              classNames(
                                "relative select-none py-2 px-3 ghostinspector-new-reservation-site-li",
                                option.description ? "opacity-50" : "cursor-pointer",
                                selected ? "bg-green-50" : active ? "bg-green-600 text-white" : "text-gray-900",
                              )
                            }
                          >
                            {({ active, selected }) => (
                              <div className="flex justify-between">
                                <div className="flex flex-col">
                                  <span className={classNames("grow truncate text-md", selected && "font-semibold")}>
                                    {option.title}
                                  </span>
                                  <span
                                    className={classNames(
                                      "truncate text-red-400 text-xs",
                                      active ? "text-green-200" : "text-gray-500",
                                    )}
                                  >
                                    {option.description}
                                  </span>
                                </div>
                                <SiteIcons
                                  site={option.site}
                                  size="10"
                                  isDropdown
                                  campingStyle={(campingStyles as CampingStyleProps[]).find(
                                    (style) => style.id == option?.site?.camping_style_id,
                                  )}
                                  lengthUnit={camp.length_unit}
                                />
                              </div>
                            )}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </>
                  )}
                </div>
              </>
            )}
          </Combobox>
        )}
      </div>
    </>
  )
}

export default SitesDropdown
